<template>
	<div class="quote-card">
		<QuoteCardTitle>FAQs</QuoteCardTitle>

		<div class="divide-y divide-slate-200">
			<!-- FAQ 1: Should I buy stock today? -->
			<div class="py-6">
				<h3 class="font-semibold text-lg mb-2 text-black">
					Should I buy {{ props.symbol || "this" }} stock today?
				</h3>
				<p class="text-slate-500 text-base leading-relaxed">
					Based on current analyst ratings, {{ props.name || "this stock" }} has a
					"{{ props.analystRatingLabel || "Unknown" }}" consensus. However,
					investment decisions should be based on your financial goals, risk
					tolerance, and portfolio strategy. Consider consulting with a financial
					advisor before making investment decisions.
				</p>
			</div>

			<!-- FAQ 2: Does stock pay dividends? -->
			<div class="py-6">
				<h3 class="font-semibold text-lg mb-2 text-black">
					Does {{ props.symbol || "this stock" }} pay dividends?
				</h3>
				<p
					class="text-slate-500 text-base leading-relaxed"
					v-html="
						props.dividendsData?.pay_dividends_description || 'No data available.'
					"
				></p>

				<!-- Dividend Information Section -->
				<template v-if="props.dividendsData?.current_period">
					<div class="mt-6">
						<h3 class="font-semibold text-lg mb-4 text-black">
							{{ props.name || "Stock" }} ({{ props.symbol || "N/A" }}) Dividend
							History
						</h3>

						<!-- Dividend Summary Table -->
						<div class="bg-white mb-6">
							<div class="flex flex-wrap gap-4 lg:gap-8">
								<div class="flex flex-col gap-2">
									<span class="text-sm text-slate-900">Ex-Dividend Date</span>
									<span class="text-2xl font-medium text-slate-900">
										{{
											formatDate(props.dividendsData?.current_period?.ex_dividend_rate)
										}}
									</span>
								</div>
								<div
									v-if="
										props.dividendYield != null &&
										props.dividendYield != undefined &&
										props.dividendYield !== '' &&
										props.dividendYield !== '0' &&
										!props.dividendYield.toString().includes('NaN')
									"
									class="flex flex-col gap-2"
								>
									<span class="text-sm text-slate-900">Dividend Yield</span>
									<span class="text-2xl font-medium text-slate-900">
										{{ dividendYieldComputed }}
									</span>
								</div>
								<div class="flex flex-col gap-2">
									<span class="text-sm text-slate-900">Annual Dividend</span>
									<span class="text-2xl font-medium text-slate-900">
										${{
											Number(
												props.dividendsData?.current_period?.annual_dividend || 0
											).toFixed(2)
										}}
									</span>
								</div>
								<div
									v-if="
										props.peRatio != null &&
										props.peRatio != undefined &&
										props.peRatio !== '' &&
										props.peRatio !== '0' &&
										!props.peRatio.toString().includes('NaN')
									"
									class="flex flex-col gap-2"
								>
									<span class="text-sm text-slate-900">P/E Ratio</span>
									<span class="text-2xl font-medium text-slate-900">
										{{
											typeof props.peRatio === "string"
												? parseFloat(props.peRatio).toFixed(2)
												: Number(props.peRatio).toFixed(2)
										}}
									</span>
								</div>
							</div>
						</div>

						<!-- Dividend History Table -->
						<div
							v-if="
								props.dividendsData?.dividend_historic &&
								props.dividendsData.dividend_historic.length > 0
							"
							class="overflow-auto border border-slate-200 rounded-lg scrollbar-hide"
						>
							<table class="w-full overflow-x-auto">
								<thead class="bg-slate-50">
									<tr>
										<th
											class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
										>
											Ex/EFF Date
										</th>
										<th
											class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
										>
											Type
										</th>
										<th
											class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
										>
											Cash Amount
										</th>
										<th
											class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
										>
											Declaration Date
										</th>
										<th
											class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
										>
											Record Date
										</th>
										<th
											class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
										>
											Payment Date
										</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="(dividend, dividendIdx) in props.dividendsData
											?.dividend_historic || []"
										:key="dividendIdx"
										class="border-b border-slate-100"
									>
										<td class="px-4 py-3 text-slate-700">
											{{ formatDate(dividend?.ex_date) }}
										</td>
										<td class="px-4 py-3 text-slate-700 capitalize">
											{{ dividend?.type || "N/A" }}
										</td>
										<td class="px-4 py-3 text-slate-700">
											${{ Number(dividend?.amount || 0).toFixed(2) }}
										</td>
										<td class="px-4 py-3 text-slate-700">
											{{ formatDate(dividend?.declared_date) }}
										</td>
										<td class="px-4 py-3 text-slate-700">
											{{ formatDate(dividend?.record_date) }}
										</td>
										<td class="px-4 py-3 text-slate-700">
											{{ formatDate(dividend?.payment_date) }}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</template>
			</div>

			<!-- FAQ 3: When does stock report earnings? -->
			<div class="py-6">
				<h3 class="font-semibold text-lg mb-2 text-black">
					When does {{ props.symbol || "this stock" }} report earnings?
				</h3>

				<p class="text-slate-500 text-base">
					{{ earningsDate }}
				</p>
			</div>

			<!-- FAQ 4: What is the stock symbol? -->
			<div class="py-6">
				<h3 class="font-semibold text-lg mb-2 text-black">
					What is the stock symbol for {{ props.name || "this company" }}?
				</h3>
				<p class="text-slate-500 text-base leading-relaxed">
					The stock symbol for {{ props.name || "this company" }} is
					{{ props.symbol || "N/A" }}.
				</p>
			</div>
		</div>
	</div>
</template>

<script setup>
import { isDateTodayOrFuture, formatEarningsDate } from "@/utils/stock-date";

const props = defineProps({
	dividendsData: {
		type: Object,
		default: () => ({})
	},
	dividendYield: {
		type: Number,
		default: 0
	},
	peRatio: {
		type: Number,
		default: 0
	},
	faqData: {
		type: Object,
		default: () => ({})
	},
	analystRatingLabel: {
		type: String,
		default: ""
	},
	name: {
		type: String,
		default: ""
	},
	symbol: {
		type: String,
		default: ""
	},
	nextEarningsDate: {
		type: String,
		default: ""
	}
});

// Helper function for date formatting
const formatDate = (dateString) => {
	if (!dateString || dateString === "N/A") {
		return "N/A";
	}
	try {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			month: "2-digit",
			day: "2-digit",
			year: "numeric"
		});
	} catch (error) {
		return "N/A";
	}
};

const earningsDate = computed(() => {
	if (
		props.nextEarningsDate !== "" &&
		isDateTodayOrFuture(props.nextEarningsDate)
	) {
		return (
			props.name +
			`'s next earnings date is ${formatEarningsDate(props.nextEarningsDate)}.`
		);
	}
	return `We don't currently have information about ${props.name}'s next earnings date.
					Check back soon as earnings dates are typically announced a few weeks in
					advance.`;
});

const dividendYieldComputed = computed(() => {
	// Check if dividendYield exists and is valid
	if (
		props.dividendYield &&
		props.dividendYield !== "" &&
		props.dividendsData.current_period.annual_dividend
	) {
		return props.dividendYield;
	}

	return "N/A";
});
</script>

<style scoped>
.scrollbar-hide {
	/* Firefox */
	scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
	/* Chrome, Safari and Opera */
	display: none;
}
</style>
